import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Logo from '../common/Logo';
import { FiTrendingUp, FiCrosshair, FiClock, FiStar, FiChevronRight } from 'react-icons/fi';
import { userData, currentPath } from '../data/mockData';
import ChallengeModal from '../components/ChallengeModal';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const [isChallengeOpen, setIsChallengeOpen] = useState(false);

  return (
    <div className="space-y-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tight">
            SYSTEM: <span className="text-quest-500">OPTIMAL</span>
          </h1>
          <p className="text-gray-500 font-mono text-sm uppercase tracking-widest">Welcome back, {userData.name} // Session 1,024</p>
        </div>

        <div className="flex gap-4">
          <div className="bg-navy-900 border border-navy-800 p-4 rounded-2xl flex items-center gap-4 shadow-xl">
            <div className="bg-trophy-500/10 p-3 rounded-xl border border-trophy-500/20">
              <SafeIcon icon={FiTrendingUp} className="text-xl text-trophy-500" />
            </div>
            <div>
              <div className="text-[10px] text-gray-500 font-mono uppercase tracking-widest">Streak</div>
              <div className="font-black text-xl text-white">{userData.streak} DAYS</div>
            </div>
          </div>
          <div className="bg-navy-900 border border-navy-800 p-4 rounded-2xl flex items-center gap-4 shadow-xl">
            <div className="bg-neon-blue/10 p-3 rounded-xl border border-neon-blue/20">
              <SafeIcon icon={FiStar} className="text-xl text-neon-blue" />
            </div>
            <div>
              <div className="text-[10px] text-gray-500 font-mono uppercase tracking-widest">Level {userData.level}</div>
              <div className="font-black text-xl text-white">{userData.xp} XP</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Action Area (Daily Boss) */}
        <div className="lg:col-span-2">
          <motion.div 
            whileHover={{ y: -4 }}
            className="relative h-full overflow-hidden rounded-3xl bg-navy-900 border border-navy-800 group cursor-pointer shadow-2xl" 
            onClick={() => setIsChallengeOpen(true)}
          >
            {/* Visual Background Repair */}
            <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
              <img 
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80" 
                alt="Cyber Background" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent" />
            
            <div className="p-10 relative z-10 flex flex-col h-full justify-between min-h-[350px]">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-quest-500/10 border border-quest-500/30 text-[10px] font-mono text-quest-400 mb-6 uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full bg-quest-500 animate-pulse" /> Daily Mission Active
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-4 text-white leading-tight">HOCs vs Hooks Encounter</h2>
                <p className="text-gray-400 max-w-md text-lg leading-relaxed">Defeat today's logic puzzle to maintain your streak and earn bonus experience points.</p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mt-10">
                <button className="w-full sm:w-auto bg-quest-500 hover:bg-quest-400 text-navy-950 font-black py-4 px-8 rounded-2xl transition-all shadow-glow-green transform group-hover:scale-105">
                  ENGAGE PROTOCOL
                </button>
                <div className="flex items-center gap-2 text-sm text-gray-500 font-mono">
                  <SafeIcon icon={FiClock} />
                  <span>RESETS IN 14H 22M</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Current Path Mini */}
        <div className="lg:col-span-1 bg-navy-900 rounded-3xl border border-navy-800 p-8 flex flex-col shadow-2xl">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-navy-800 border border-navy-700 flex items-center justify-center">
                <SafeIcon icon={FiCrosshair} className="text-quest-500 text-xl" />
              </div>
              <h3 className="font-black text-xl text-white uppercase tracking-tight">Active Node</h3>
            </div>
            <Link to="/map" className="p-2 text-gray-500 hover:text-quest-500 transition-colors">
              <SafeIcon icon={FiChevronRight} className="text-2xl" />
            </Link>
          </div>

          <div className="mb-10">
            <div className="text-xs text-gray-500 font-mono uppercase tracking-widest mb-2">{currentPath.title}</div>
            <div className="flex justify-between items-end mb-3">
              <span className="font-black text-3xl text-white">{currentPath.progress}%</span>
              <span className="text-[10px] text-gray-500 font-mono uppercase">Node 04 / 06</span>
            </div>
            <div className="h-3 w-full bg-navy-950 rounded-full overflow-hidden border border-navy-800 p-[2px]">
              <motion.div 
                initial={{ width: 0 }} 
                animate={{ width: `${currentPath.progress}%` }} 
                transition={{ duration: 1.5, ease: "easeOut" }} 
                className="h-full bg-quest-500 rounded-full shadow-glow-green" 
              />
            </div>
          </div>

          <div className="flex-1 space-y-4">
            {currentPath.nodes.slice(2, 5).map((node, idx) => (
              <div 
                key={node.id} 
                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                  node.status === 'current' 
                    ? 'bg-navy-800 border-quest-500 shadow-[0_0_15px_rgba(0,229,115,0.1)]' 
                    : node.status === 'completed' 
                    ? 'bg-navy-800/40 border-navy-700 opacity-60' 
                    : 'bg-navy-950/50 border-navy-800 opacity-30'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 font-mono font-black ${
                  node.status === 'current' ? 'bg-quest-500 border-quest-500 text-navy-950 shadow-glow-green' : 'border-navy-700 text-gray-600'
                }`}>
                  {idx + 3}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold truncate uppercase tracking-tight ${node.status === 'current' ? 'text-white' : 'text-gray-500'}`}>
                    {node.title}
                  </div>
                  <div className="text-[10px] font-mono text-gray-600 uppercase">{node.type}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <ChallengeModal isOpen={isChallengeOpen} onClose={() => setIsChallengeOpen(false)} />
    </div>
  );
}